import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  ClipboardCopyButton,
  CodeBlock,
  CodeBlockAction,
  CodeBlockCode,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  List,
  ListItem,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  ExclamationCircleIcon,
  InfoCircleIcon,
  MagicIcon,
  ShieldAltIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useMailDomainSpf } from '../../../hooks/useMailDomainSpf';
import { parseSpf } from './spfParse';
import SpfWizardModal from './SpfWizardModal';

interface SpfTabProps {
  mailDomainId: number;
  organizationDomainId: number;
  domainName: string;
}

const SOURCE_LABELS: Record<string, string> = {
  txt: 'TXT record',
  spf: 'SPF-type record (legacy)',
};

/**
 * Shows the apex SPF policy published for a mail domain: the raw record string, its decomposition into
 * permitted senders + final policy (via {@link parseSpf}), and advisories for common misconfigurations.
 *
 * Owns its own best-effort live DNS read via {@link useMailDomainSpf}, so it must be mounted lazily —
 * the parent `<Tabs>` sets `mountOnEnter` and this tab provides a Refresh action for manual re-runs. A
 * DNS read that fails is not a request error: `spf_state` is `'unknown'` and the backend's safe
 * `spf_error` is surfaced. The "Build SPF record" button opens a (currently placeholder) wizard.
 */
function SpfTab({ mailDomainId, organizationDomainId, domainName }: SpfTabProps) {
  const { data, loading, error, refetch } = useMailDomainSpf(mailDomainId);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(
    () => (data?.spf_state === 'configured' && data.value ? parseSpf(data.value) : null),
    [data],
  );

  const primaryRecord = useMemo(() => {
    if (!data) return null;
    return data.records.find((r) => r.source === data.source) ?? data.records[0] ?? null;
  }, [data]);

  const onCopy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
  };

  const renderToolbar = () => (
    <Toolbar>
      <ToolbarContent>
        <ToolbarItem>
          <Button variant="primary" icon={<MagicIcon />} onClick={() => setIsWizardOpen(true)}>
            Build SPF record
          </Button>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="secondary" icon={<SyncAltIcon />} onClick={() => refetch()} isLoading={loading}>
            Refresh
          </Button>
        </ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const renderPolicy = () => {
    if (parsed?.policy) {
      return (
        <>
          <Label status={parsed.policy.severity}>{parsed.policy.label}</Label>
          <Content component="p" className="pf-v6-u-mt-sm">
            {parsed.policy.description}
          </Content>
        </>
      );
    }
    return (
      <Content component="p">
        This record specifies no <code>all</code> mechanism, so unmatched senders fall through to a neutral
        result (no protection).
      </Content>
    );
  };

  const renderConfigured = () => {
    if (!data) return null;

    // Advisories: the multiple-records breakage (only the backend can see it) plus the per-record
    // decomposition warnings from parseSpf.
    const advisories = parsed?.warnings ?? [];

    return (
      <>
        {data.has_multiple && (
          <Alert
            variant="danger"
            isInline
            title="Multiple SPF records are published"
            className="pf-v6-u-mb-md"
          >
            A domain may publish only one SPF record. With more than one, receivers return a permanent
            error (<code>permerror</code>) and SPF stops protecting the domain. Merge them into a single
            record.
          </Alert>
        )}

        {parsed && !parsed.valid && (
          <Alert variant="warning" isInline title="Could not interpret the record" className="pf-v6-u-mb-md">
            The published value does not look like a valid SPF record. The raw value is shown below.
          </Alert>
        )}

        {advisories.length > 0 && (
          <Alert variant="warning" isInline title="Recommendations" className="pf-v6-u-mb-md">
            {advisories.length === 1 ? (
              advisories[0]
            ) : (
              <List>
                {advisories.map((w, i) => (
                  <ListItem key={i}>{w}</ListItem>
                ))}
              </List>
            )}
          </Alert>
        )}

        <Card>
          <CardTitle component="h2">Published SPF record</CardTitle>
          <CardBody>
            <CodeBlock
              actions={
                <CodeBlockAction>
                  <ClipboardCopyButton
                    id="spf-copy-button"
                    aria-label="Copy SPF record to clipboard"
                    variant="plain"
                    onClick={() => onCopy(data.value ?? '')}
                    exitDelay={copied ? 1500 : 600}
                    maxWidth="110px"
                    onTooltipHidden={() => setCopied(false)}
                  >
                    {copied ? 'Copied!' : 'Copy to clipboard'}
                  </ClipboardCopyButton>
                </CodeBlockAction>
              }
            >
              <CodeBlockCode>{data.value}</CodeBlockCode>
            </CodeBlock>

            <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-md">
              <DescriptionListGroup>
                <DescriptionListTerm>Zone apex</DescriptionListTerm>
                <DescriptionListDescription>{data.domain}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Record type</DescriptionListTerm>
                <DescriptionListDescription>
                  {data.source ? SOURCE_LABELS[data.source] ?? data.source : '—'}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>TTL</DescriptionListTerm>
                <DescriptionListDescription>
                  {primaryRecord ? `${primaryRecord.ttl} seconds` : '—'}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>

        {parsed?.valid && (
          <Card className="pf-v6-u-mt-md">
            <CardTitle component="h2">Decomposition</CardTitle>
            <CardBody>
              <Content component="h3" className="pf-v6-u-font-size-sm pf-v6-u-mb-xs">
                Final policy
              </Content>
              {renderPolicy()}

              <Content component="h3" className="pf-v6-u-font-size-sm pf-v6-u-mt-lg pf-v6-u-mb-sm">
                Permitted senders
              </Content>
              {parsed.mechanisms.length > 0 ? (
                <Table aria-label="SPF permitted senders" variant="compact">
                  <Thead>
                    <Tr>
                      <Th width={15}>Result</Th>
                      <Th width={30}>Mechanism</Th>
                      <Th>Meaning</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {parsed.mechanisms.map((m, i) => (
                      <Tr key={i}>
                        <Td dataLabel="Result">{m.qualifierLabel}</Td>
                        <Td dataLabel="Mechanism">
                          <code style={{ wordBreak: 'break-all' }}>{m.raw}</code>
                        </Td>
                        <Td dataLabel="Meaning" modifier="wrap">
                          {m.description}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Content component="p">No senders are explicitly authorized by this record.</Content>
              )}

              {parsed.redirect && (
                <Content component="p" className="pf-v6-u-mt-md">
                  This record delegates evaluation to <code>{parsed.redirect}</code> via a{' '}
                  <code>redirect</code> modifier.
                </Content>
              )}
            </CardBody>
          </Card>
        )}
      </>
    );
  };

  const renderBody = () => {
    // Initial load only — a refetch keeps the existing view visible and surfaces progress on the
    // Refresh button instead of replacing the whole tab with a spinner.
    if (loading && !data) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load SPF record"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (!data) return null;

    if (data.spf_state === 'unknown') {
      // No toolbar here: SPF can't be published into a zone that isn't managed, so "Build" is hidden and
      // the only action — Refresh, to re-attempt the DNS read — lives in the empty-state footer (DKIM pattern).
      return (
        <EmptyState titleText="SPF status unavailable" icon={InfoCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            {data.spf_error ?? 'The SPF record could not be determined for this domain.'}
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="link" icon={<SyncAltIcon />} onClick={() => refetch()} isLoading={loading}>
                Refresh
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (data.spf_state === 'not_configured') {
      return (
        <>
          {renderToolbar()}
          <EmptyState titleText="No SPF record published" icon={ShieldAltIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              No SPF policy is published on <strong>{data.domain}</strong>. Publishing one tells receiving
              servers which hosts may send mail for this domain and helps prevent spoofing.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" icon={<MagicIcon />} onClick={() => setIsWizardOpen(true)}>
                  Build SPF record
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </>
      );
    }

    return (
      <>
        {renderToolbar()}
        {renderConfigured()}
      </>
    );
  };

  return (
    <>
      {renderBody()}
      <SpfWizardModal
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        organizationDomainId={organizationDomainId}
        domainName={domainName}
        currentValue={data?.value ?? null}
        onPublished={() => void refetch()}
      />
    </>
  );
}

export default SpfTab;
